<template>
  <div class="route-builder-map">
    <LoadingIndicator
      class="map-loading-indicator"
      :loading="isDynamicMapLoading"
    />
    <div
      :id="MAP_ID"
      class="map"
      :class="mapRenderMethodClass"
      tabindex="-1"
    ></div>
    <div :id="MOUNT_POINT_ID" tabindex="-1" hidden></div>
  </div>
</template>

<script lang="ts" setup>
/* eslint-disable vue/one-component-per-file */
import 'leaflet/dist/leaflet.css';

import { default as axios, CancelTokenSource } from 'axios';
import { default as L } from 'leaflet';
import debounce from 'lodash/debounce';
import type { PropType, Ref, ShallowRef } from 'vue';
import {
  computed,
  h,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { Optional } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import { useMapDetails } from '@/composables';
import { Mileages } from '@/composables/routeBuilder';
import {
  MapRenderMethod,
  ShipmentStopType,
  ShippingPoint,
  StopMileageResponse,
} from '@/model';
import {
  ProposedShipment,
  ProposedShipmentMapDetails,
} from '@/model/routeBuilder';
import { RoutePlanProposedShipment } from '@/model/routeBuilder/phase2';

import ShipmentVisualizerNode from '@/components/routeBuilder/visualizer/ShipmentVisualizerNode.vue';
import StopPopup from '@/components/shipment/map/StopPopup.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';

const MAP_ID = 'route-builder-map';
const MOUNT_POINT_ID = 'route-builder-map-hidden-mount-point';

const props = defineProps({
  proposedShipments: {
    type: Array as PropType<ProposedShipment[] | RoutePlanProposedShipment[]>,
    default: () => [],
  },
  mileages: {
    type: Object as PropType<Mileages>,
    default: () => ({}),
  },
  highlightedShipmentIdentifiers: {
    type: Array as PropType<Array<RoutePlanProposedShipment['identifier']>>,
    default: () => [],
  },
  showPopInButton: {
    type: Boolean,
    default: () => false,
  },
});
const {
  proposedShipments,
  mileages,
  highlightedShipmentIdentifiers,
  showPopInButton,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'pop-in'): void;
}>();

const map: ShallowRef<Optional<L.Map>> = shallowRef();

const { t } = useI18n();
const {
  centerMapOnAmerica,
  fitBoundsOptions,
  generateElementFromComponent,
  generateHtmlFromComponent,
  generateStopIcon,
  initMap,
  isDynamicMapLoading,
  mapRenderMethodClass,
  toLatLngTuple,
  toOptionalLatLngTuple,
} = useMapDetails(map, { initialMapRenderMethod: MapRenderMethod.LEGACY });

// NOTE: shallowRef (not ref) for Leaflet objects — their internal layer/LatLng
// graphs must not enter Vue's deep reactivity system, or @vue/reactivity will
// recursively proxy and retain tens of thousands of LatLng/Point objects
// (memory leak). The same applies to any future Leaflet refs added here.
/** A layer that contains a child layer for each shipment */
const shipmentsLayer: ShallowRef<Optional<L.FeatureGroup>> = shallowRef();
const shipmentsByODPair: Ref<
  Map<string, ProposedShipment | RoutePlanProposedShipment>
> = ref(new Map());
let getMapDetailsCancelToken: Optional<CancelTokenSource> = undefined;

const pickDropSequenceNumbers = computed(() => {
  const entries = proposedShipments.value.map(
    (s): [shipmentIdentifier: number, pickDropSeqNums: number[]] => {
      let picks = 0;
      let drops = 0;

      const pickDropSeqNums = s.stops.map((stop) =>
        stop.isPickup ? ++picks : ++drops,
      );
      return [s.identifier, pickDropSeqNums];
    },
  );

  return Object.fromEntries(entries);
});
const generatePopInButtonEl = () => {
  return generateElementFromComponent(
    {
      render: () =>
        h(RygenButton, {
          mode: 'tertiary',
          label: t('routeBuilder.popIn'),
          icon: 'pi pi-window-minimize',
          onClick: () => emit('pop-in'),
        }),
    },
    `#${MOUNT_POINT_ID}`,
  );
};

const generateStopPickDropSeqNum = (
  shipment: ProposedShipment | RoutePlanProposedShipment,
  stop: ShippingPoint,
) => {
  // Use `sequenceNumber` as a fallback if we can't computed a pickDropSeqNum
  // We should always have a sequence number at this point so the -1 is mostly
  // to force typescript to infer `number` type
  const seqNum = stop.sequenceNumber ?? -1;
  const stopIndex = shipment.stops.findIndex(
    (s) => s.sequenceNumber === stop.sequenceNumber,
  );

  const pickDropSequenceNumbersForShipment =
    pickDropSequenceNumbers.value[shipment.identifier];
  const pickDropSeqNum: Optional<number> =
    pickDropSequenceNumbersForShipment[stopIndex];

  const prefix =
    stop.stopType === ShipmentStopType.PICKUP
      ? t('shipments.pickInitial')
      : t('shipments.dropInitial');

  return pickDropSeqNum !== undefined
    ? `S${shipment.identifier}:${prefix}${pickDropSeqNum}`
    : `S${shipment.identifier}:${seqNum}`;
};

const generateStopPopupHtml = (
  shipment: ProposedShipment | RoutePlanProposedShipment,
  stop: ShippingPoint,
  isMissingCoords = false,
) => {
  return generateHtmlFromComponent(
    {
      render: () =>
        h(StopPopup, {
          id: `shipment-${shipment.identifier}-stop-${stop.sequenceNumber}`,
          stop: stop,
          lineItems: shipment.getLineItemsForStop(stop),
          stopLabel: generateStopPickDropSeqNum(shipment, stop),
          isMissingCoords,
        }),
    },
    `#${MOUNT_POINT_ID}`,
  );
};

const generateShipmentPopupHtml = (
  shipment: ProposedShipment | RoutePlanProposedShipment,
  mileage?: StopMileageResponse,
) => {
  return generateHtmlFromComponent(
    {
      render: () =>
        h(ShipmentVisualizerNode, {
          shipmentIdentifier: shipment.identifier,
          mileage: mileage?.mileage,
        }),
    },
    `#${MOUNT_POINT_ID}`,
  );
};

const generateShipmentKey = (
  proposedShipment: ProposedShipment | RoutePlanProposedShipment,
) => {
  const origin = proposedShipment.origin?.location.addressString;
  const destination = proposedShipment.destination?.location.addressString;

  return `${origin}-${destination}`;
};

const generateLayerForProposedShipment = (
  mapDetails: ProposedShipmentMapDetails,
): L.LayerGroup => {
  const index = proposedShipments.value.findIndex(
    (s) => s.identifier === mapDetails.shipmentIdentifier,
  );
  if (index === -1) {
    throw new Error('Could not find proposed shipment for map detail response');
  }
  const proposedShipment = proposedShipments.value[index];
  const stops = proposedShipment.stops;
  const destinationIndex = stops.length - 1;
  const mileageForStops = mileages.value[mapDetails.shipmentIdentifier];

  const layerForShipment = L.featureGroup();

  mapDetails.stopCoordinates.forEach((point, i) => {
    // If the API failed to geocode the stop, `point` will be `undefined`
    const isMissingCoords = !point;
    const latLngTuple = toLatLngTuple(point);
    const stop = stops[i];

    const classNames = ['stop', `proposed-shipment-${stop.shipmentIdentifier}`];
    classNames.push(
      stop.stopType === ShipmentStopType.PICKUP ? 'pickup' : 'dropoff',
    );
    if (i === 0) {
      classNames.push('origin');
    } else if (i === destinationIndex) {
      classNames.push('destination');
    }

    if (isMissingCoords) {
      const errorClassName = stop.location.locationName
        ? 'has-geocode-error'
        : 'missing-location';
      classNames.push(errorClassName);
    }

    const icon = generateStopIcon(classNames.join(' '));
    const popupContent = generateStopPopupHtml(
      proposedShipment,
      stop,
      isMissingCoords,
    );
    L.marker(latLngTuple, { icon: icon })
      .addTo(layerForShipment)
      .bindPopup(popupContent, { closeButton: false })
      .on('mouseover', (event) => event.target.openPopup())
      .on('mouseout', (event) => event.target.closePopup());
  });

  mapDetails.legCoordinates.map((coords, index) => {
    const classNames = [
      'route',
      `proposed-shipment-${proposedShipment.identifier}`,
    ];
    const originSequenceNumber = index + 1;
    const mileage = mileageForStops?.response.data?.find(
      (m) => m.originSequenceNumber === originSequenceNumber,
    );

    const mappedPoints = coords
      .map((c) => toOptionalLatLngTuple(c))
      .filter(notNull);
    const shipmentPopupContent = generateShipmentPopupHtml(
      proposedShipment,
      mileage,
    );

    const shipmentKey = generateShipmentKey(proposedShipment);
    const isDuplicateRoute = shipmentsByODPair.value.has(shipmentKey);
    if (isDuplicateRoute) {
      classNames.push('duplicate');
    } else {
      shipmentsByODPair.value.set(shipmentKey, proposedShipment);
    }

    L.polyline(mappedPoints, {
      weight: 4,
      color: proposedShipment.color,
      dashArray: isDuplicateRoute ? '12' : undefined,
      className: classNames.join(' '),
    })
      .addTo(layerForShipment)
      .bindPopup(shipmentPopupContent)
      .on('mouseover', (event) => event.target.openPopup(event.latlng))
      .on('mouseout', (event) => event.target.closePopup());
  });

  return layerForShipment;
};

const redrawMap = async () => {
  if (!map.value) {
    console.error(
      'Cannot redraw map without a map instance. Try calling `init` first.',
    );
    return;
  }

  if (!shipmentsLayer.value) {
    console.error(
      'Cannot redraw map without a shipment layer. Try calling `init` first.',
    );
    return;
  }

  getMapDetailsCancelToken?.cancel();
  getMapDetailsCancelToken = axios.CancelToken.source();

  const validMap = map.value;
  const shipmentLayer = shipmentsLayer.value;
  isDynamicMapLoading.value = true;
  shipmentsByODPair.value.clear();

  try {
    if (proposedShipments.value.length) {
      const mapDetails = await api.shipmentPlanning.getMapDetails(
        proposedShipments.value,
        getMapDetailsCancelToken,
      );
      mapDetails.details.map((details) => {
        const layer = generateLayerForProposedShipment(details);
        layer.addTo(shipmentLayer);
      });
    }

    const layers = shipmentLayer.getLayers();

    if (layers.length) {
      validMap.fitBounds(shipmentLayer.getBounds(), fitBoundsOptions);
    } else {
      centerMapOnAmerica();
    }
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(e);
    centerMapOnAmerica();
  } finally {
    isDynamicMapLoading.value = false;
  }
};
const redrawMapDebounced = debounce(redrawMap, 500);

const init = () => {
  map.value = initMap(MAP_ID);
  shipmentsLayer.value = L.featureGroup().addTo(map.value);

  if (showPopInButton.value) {
    const PopInButton = L.Control.extend({
      onAdd(_map: L.Map) {
        return generatePopInButtonEl();
      },
      onRemove(_map: L.Map) {
        // Nothing to do here
      },
    });
    const popInButton = new PopInButton({ position: 'topright' });
    popInButton.addTo(map.value);
  }
};

watch(
  proposedShipments,
  () => {
    if (map.value && shipmentsLayer.value) {
      shipmentsLayer.value.removeFrom(map.value);
      shipmentsLayer.value = L.featureGroup().addTo(map.value);
    }

    redrawMapDebounced();
  },
  { deep: true },
);

watch(
  highlightedShipmentIdentifiers,
  (newVal) => {
    const container = map.value?.getContainer();
    if (!container) {
      return;
    }

    container
      .querySelectorAll('.highlight')
      .forEach((el) => el.classList.remove('highlight'));

    newVal.forEach((shipmentIdentifier) => {
      container
        .querySelectorAll(
          `.stop.proposed-shipment-${shipmentIdentifier}, .route.proposed-shipment-${shipmentIdentifier}`,
        )
        .forEach((el) => el.classList.add('highlight'));
    });
  },
  { deep: true, immediate: true },
);

onBeforeUnmount(() => {
  map.value?.off();
  map.value?.remove();
  getMapDetailsCancelToken?.cancel();
  redrawMapDebounced.cancel();
});

onMounted(() => {
  init();
  redrawMapDebounced();
});

defineExpose({
  init,
  redraw: redrawMapDebounced,
});
</script>

<style>
.route-builder-map {
  position: relative;
  background: var(--map-background);
  height: 100%;

  .map-loading-indicator {
    position: absolute;
    top: 25%;
    left: calc(50% - (3rem / 2));
    font-size: 3rem;
    color: white;
    z-index: 1;
  }

  .map {
    background: inherit;
    overflow: hidden;
    z-index: 0;
    height: 100%;

    &:has(.highlight) {
      .stop:not(.highlight),
      .route:not(.highlight) {
        opacity: 0.5;
        filter: saturate(0.3);
      }
    }

    &.leaflet-container {
      background: inherit;
      width: 100%;
    }

    .leaflet-popup {
      .leaflet-popup-content-wrapper {
        background: var(--p-gray-600);
        border-radius: var(--p-border-radius-sm);
        color: white;

        .leaflet-popup-content {
          margin: 0;

          .stop-popup {
            --p-avatar-font-size: 12px;
            --p-avatar-width: 36px;
            --p-avatar-height: 36px;
          }
        }
      }
    }

    .leaflet-marker-icon {
      &:is(.origin, .destination) {
        .pi-map-marker {
          font-size: 28px;
        }
      }

      &.has-geocode-error {
        --stop-type-accent-color: var(--danger-color);
      }

      &.missing-location {
        --stop-type-accent-color: var(--warning-color);
      }

      .pi-map-marker {
        font-size: 16px;
        color: var(--stop-type-accent-color);
      }
    }
  }
}
</style>
